/**
 * Cartao "Dados do lead": e-mail, oferta, funil, plataforma e id externo.
 *
 * Lista de definicao (`dl`) em vez de tabela: sao pares rotulo/valor, e o
 * leitor de tela anuncia "E-mail: ..." sem cabecalho de coluna. O id externo
 * tem botao de copiar porque e o que a pessoa cola no painel do provedor para
 * achar o pedido -- a copia e local (clipboard), nao chamada de rede.
 */

import { useEffect, useState } from 'react';
import { Badge, IconButton, SectionCard } from '../../../components/ui';
import { formatBRLFromCents, PROVIDER_LABEL, VALOR_AUSENTE } from '../../../lib/format';
import type { ProductOffer } from '../../../types';
import type { LeadDetail } from '../types';
import { FATO, FUNIL_LABEL } from '../vocabulario';

const COPIADO_VISIVEL_MS = 1_600;

export function CartaoDadosDoLead({ lead, oferta }: { readonly lead: LeadDetail; readonly oferta: ProductOffer | null }): JSX.Element {
  const [copiado, setCopiado] = useState(false);

  useEffect(() => {
    if (!copiado) {
      return undefined;
    }
    const timer = window.setTimeout(() => setCopiado(false), COPIADO_VISIVEL_MS);
    return () => window.clearTimeout(timer);
  }, [copiado]);

  const copiar = (): void => {
    if (typeof navigator.clipboard?.writeText !== 'function') {
      return;
    }
    void navigator.clipboard.writeText(lead.externalId).then(
      () => setCopiado(true),
      () => setCopiado(false),
    );
  };

  return (
    <SectionCard id="dados-do-lead" title="Dados do lead" icon="leads">
      <dl className="flex flex-col gap-3 text-sm">
        <div className="flex flex-col gap-0.5">
          <dt className="text-xs text-ink-subtle">E-mail</dt>
          <dd className="break-all text-ink">{lead.email ?? VALOR_AUSENTE}</dd>
        </div>
        <div className="flex flex-col gap-0.5">
          <dt className="text-xs text-ink-subtle">Oferta</dt>
          <dd className="text-ink">
            {lead.productName ?? VALOR_AUSENTE}
            <span className="mt-1 flex flex-wrap items-center gap-1.5">
              {oferta === null ? null : <Badge tone="neutral">{formatBRLFromCents(oferta.priceCents)}</Badge>}
              {oferta === null ? null : <Badge tone={oferta.recurring ? 'brand' : 'neutral'}>{oferta.recurring ? 'Assinatura' : 'Compra única'}</Badge>}
              {oferta !== null && !oferta.active ? <Badge tone="warning">Oferta inativa</Badge> : null}
              {lead.highValue ? (
                <Badge tone="accent" icon="estrela">
                  Alto valor
                </Badge>
              ) : null}
            </span>
          </dd>
        </div>
        <div className="flex flex-col gap-0.5">
          <dt className="text-xs text-ink-subtle">Funil</dt>
          <dd className="text-ink">
            {FUNIL_LABEL[lead.funnel]}
            <span className="block text-xs text-ink-subtle">Entrou por {FATO[lead.funnel].substantivo}; nada antes disso.</span>
          </dd>
        </div>
        <div className="flex flex-col gap-0.5">
          <dt className="text-xs text-ink-subtle">Plataforma</dt>
          <dd className="text-ink">{PROVIDER_LABEL[lead.provider]}</dd>
        </div>
        <div className="flex flex-col gap-0.5">
          <dt className="text-xs text-ink-subtle">Id do pedido no provedor</dt>
          <dd className="flex items-center gap-2">
            <code className="rounded-md border border-line bg-page px-1.5 py-0.5 font-mono text-xs text-ink">{lead.externalId}</code>
            <IconButton icon="copiar" label="Copiar id do pedido" size="sm" onClick={copiar} />
            {copiado ? (
              <span role="status" className="text-xs text-status-good">
                Copiado
              </span>
            ) : null}
          </dd>
        </div>
        <div className="flex flex-col gap-0.5">
          <dt className="text-xs text-ink-subtle">Id no Converte Fácil</dt>
          <dd className="break-all font-mono text-xs text-ink-muted">{lead.id}</dd>
        </div>
      </dl>
    </SectionCard>
  );
}
